import { Link } from "@tanstack/react-router";
import { AlertTriangle, Clock, Inbox, Pin, PinOff, Plug, Plus, RotateCw, Search } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { GrupoDeEscolha } from "@/components/ui/grupo-de-escolha";
import { PanelHeader, PanelTitle } from "@/components/ui/panel";
import { ToolbarSearch } from "@/components/ui/toolbar";
import { AvatarDoResponsavel } from "@/components/usuarios/AvatarDoResponsavel";
import { CANAIS_DA_CAIXA, contaDaFoto, outraPessoaDaDireta, separarFixadas, type RecorteDaCaixa } from "@/lib/conversas";
import { iniciaisDoNome } from "@/lib/ficha-da-equipe";
import { calendarDateKey, formatClock, formatDate } from "@/lib/format";
import { contagem } from "@/lib/plural";
import type { CanalDaConversa, ConversaUnificada, EstadoDasExternas } from "@/lib/types";
import { cn } from "@/lib/utils";

import { ICONE_DO_CANAL, SeloDeMencao, SeloDoCanal } from "./selo-do-canal";
import { IconButton } from "@/components/ui/icon-button";

function quando(iso: string | null): string {
  if (!iso) return "";
  const data = new Date(iso);
  return calendarDateKey(data) === calendarDateKey(new Date()) ? formatClock(data) : formatDate(iso);
}

/**
 * O aviso discreto da fonte externa, no topo da lista. Sem o atendimento, a
 * caixa segue com a Equipe e o Portal — nunca a tela inteira de erro.
 */
function AvisoDoAtendimento({
  estado,
  podeAdministrar,
  onTentarDeNovo,
}: {
  estado: EstadoDasExternas;
  podeAdministrar: boolean;
  onTentarDeNovo: () => void;
}) {
  if (estado === "READY") return null;
  if (estado === "SEM_PERMISSAO") {
    return (
      <p className="m-0 px-4 pb-3 text-legenda text-[var(--muted)]" data-aviso-do-atendimento="sem-permissao">
        WhatsApp e e-mail ficam com quem tem a permissão “Atender conversas”.
      </p>
    );
  }
  const avisos = {
    INACTIVE: {
      Icone: Plug,
      titulo: "WhatsApp e e-mail ainda não estão ligados",
      texto: podeAdministrar
        ? "A lista mostra a Equipe e o Portal. Ative o atendimento para as conversas dos canais entrarem aqui, com o selo do canal."
        : "A lista mostra a Equipe e o Portal. Peça a um administrador para ativar o atendimento em Configurações.",
    },
    AGENT_PENDING: {
      Icone: Clock,
      titulo: "Seu acesso ao atendimento está sendo sincronizado",
      texto: "A conta do escritório já está ativa; falta um administrador concluir a sincronização do seu usuário. Até lá, a lista mostra a Equipe e o Portal.",
    },
    UNAVAILABLE: {
      Icone: AlertTriangle,
      titulo: "Não conseguimos carregar WhatsApp e e-mail agora",
      texto: "A Equipe e o Portal seguem funcionando. As conversas externas voltam assim que o atendimento responder.",
    },
  } as const;
  const { Icone, titulo, texto } = avisos[estado];
  return (
    <div
      role={estado === "UNAVAILABLE" ? "alert" : "note"}
      data-aviso-do-atendimento={estado}
      className="mx-3 mb-3 grid gap-2 rounded-2xl border border-border bg-surface-2 p-3 text-corpo"
    >
      <strong className={cn("flex items-center gap-2", estado === "UNAVAILABLE" && "text-[var(--critical)]")}>
        <Icone aria-hidden="true" className="size-4 shrink-0" />
        {titulo}
      </strong>
      <p className="m-0 text-legenda text-[var(--muted)]">{texto}</p>
      {estado === "UNAVAILABLE" ? (
        <Button type="button" variant="vidro" size="sm" className="justify-self-start" onClick={onTentarDeNovo}>
          <RotateCw aria-hidden="true" />
          Tentar novamente
        </Button>
      ) : estado === "INACTIVE" && podeAdministrar ? (
        <Button asChild variant="vidro" size="sm" className="justify-self-start">
          <Link to="/configuracoes" search={{ secao: "atendimento" }}>
            Abrir Configurações
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

function ItemDaCaixa({
  conversa,
  meuId,
  aberta,
  podeFixar,
  onAbrir,
  onFixar,
}: {
  conversa: ConversaUnificada;
  meuId: string;
  aberta: boolean;
  podeFixar: boolean;
  onAbrir: (id: string) => void;
  /** Sem ele (o chat rápido, TUT-590), o item não tem o alfinete. */
  onFixar?: (id: string, fixar: boolean) => void;
}) {
  const Icone = ICONE_DO_CANAL[conversa.canal];
  // TUT-561 — na direta, a vaga do ícone é da outra pessoa: o avatar dos
  // Prazos, decorativo (o nome é o título, logo ao lado).
  const pessoa = outraPessoaDaDireta(conversa, meuId);
  return (
    <li className="relative" data-conversa={conversa.id}>
      <button
        type="button"
        aria-current={aberta ? "true" : undefined}
        className={cn(
          "grid w-full cursor-pointer grid-cols-[2.25rem_minmax(0,1fr)] gap-3 border-0 bg-transparent py-3 pl-3 text-left transition-colors hover:bg-surface-2",
          onFixar ? "pr-11" : "pr-3",
          aberta && "bg-[var(--primary-highlight)] shadow-[inset_3px_0_0_var(--primary)]",
        )}
        onClick={() => onAbrir(conversa.id)}
      >
        {pessoa ? (
          <span data-avatar-da-conversa="" className="grid size-9 place-items-center">
            <AvatarDoResponsavel
              userId={contaDaFoto(pessoa)}
              nome={pessoa.nome}
              iniciais={iniciaisDoNome(pessoa.nome)}
              rotulo=""
              tamanho="conversa"
            />
          </span>
        ) : (
          <span aria-hidden="true" className="grid size-9 place-items-center rounded-full border border-border bg-surface-2">
            <Icone className="size-4 text-[var(--muted)]" />
          </span>
        )}
        <span className="grid min-w-0 gap-1">
          <span className="flex min-w-0 items-center gap-2">
            <strong className={cn("min-w-0 flex-1 truncate", !conversa.naoLidas && "font-medium")}>{conversa.titulo}</strong>
            <span className="shrink-0 text-legenda text-[var(--muted)]">{quando(conversa.ultimaAtividadeEm)}</span>
          </span>
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-corpo text-[var(--muted)]">{conversa.previa ?? "Sem mensagens ainda"}</span>
            {conversa.naoLidas > 0 ? (
              <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-[var(--primary)] px-1.5 text-legenda font-bold text-white">
                <span aria-hidden="true">{conversa.naoLidas > 99 ? "99+" : conversa.naoLidas}</span>
                <span className="sr-only">{contagem(conversa.naoLidas, "mensagem não lida", "mensagens não lidas")}</span>
              </span>
            ) : null}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <SeloDoCanal canal={conversa.canal} rotulo={conversa.canalRotulo} />
            {conversa.mencionouVoce ? <SeloDeMencao /> : null}
          </span>
        </span>
      </button>
      {onFixar ? (
        <IconButton
          label={conversa.fixada ? `Desafixar ${conversa.titulo}` : `Fixar ${conversa.titulo}`}
          icone={conversa.fixada ? <PinOff aria-hidden="true" /> : <Pin aria-hidden="true" />}
          aria-pressed={conversa.fixada}
          title={!conversa.fixada && !podeFixar ? "Você já tem o máximo de conversas fixadas" : undefined}
          disabled={!conversa.fixada && !podeFixar}
          onClick={() => onFixar(conversa.id, !conversa.fixada)}
          className="absolute right-1 top-2"
        />
      ) : null}
    </li>
  );
}

/**
 * I1 — a fixada que não dá para mostrar agora (sem a permissão, o atendimento
 * fora do ar, a conversa apagada) não prende a vaga em silêncio: aparece no
 * grupo das fixadas, com "Desafixar".
 */
function FixadaIndisponivel({ id, onDesafixar }: { id: string; onDesafixar: () => void }) {
  const externa = id.startsWith("cw:");
  return (
    <li className="relative" data-conversa={id} data-fixada-indisponivel>
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)] gap-3 py-3 pl-3 pr-11">
        <span aria-hidden="true" className="grid size-9 place-items-center rounded-full border border-dashed border-border">
          <PinOff className="size-4 text-[var(--muted)]" />
        </span>
        <span className="grid min-w-0 gap-1">
          <strong className="font-medium">Conversa indisponível</strong>
          <span className="text-corpo text-[var(--muted)]">
            {externa
              ? "Um atendimento que você não alcança agora (sem a permissão, ou o atendimento fora do ar)."
              : "Uma conversa que não está mais na sua lista."}
          </span>
        </span>
      </div>
      <IconButton
        label="Desafixar conversa indisponível"
        icone={<PinOff aria-hidden="true" />}
        onClick={onDesafixar}
        className="absolute right-1 top-2"
      />
    </li>
  );
}

/**
 * TUT-491 — a lista da caixa única (variação A, aprovada em 14/10): uma lista
 * só, por recência, com as fixadas no topo na ordem de fixação, como no
 * WhatsApp Web. Os recortes Todas / Minhas / Não lidas e o filtro de canal
 * moram na URL.
 */
export function ListaDaCaixa({
  itens,
  contagens,
  recorte,
  onRecorte,
  canais,
  canaisEscolhidos,
  onCanal,
  busca,
  onBusca,
  aberta,
  meuId,
  onAbrir,
  onFixar,
  podeFixarMais,
  estado,
  podeAdministrar,
  onTentarDeNovo,
  temMais,
  carregandoMais,
  onCarregarMais,
  onNovaConversa,
  onLimpar,
  indisponiveis,
  rapida,
}: {
  itens: ConversaUnificada[];
  contagens: Record<RecorteDaCaixa, number>;
  recorte: RecorteDaCaixa;
  onRecorte: (recorte: RecorteDaCaixa) => void;
  canais: CanalDaConversa[];
  canaisEscolhidos: CanalDaConversa[];
  onCanal: (canal: CanalDaConversa) => void;
  busca: string;
  onBusca: (busca: string) => void;
  aberta: string | null;
  /** TUT-561 — quem lê: na direta, o avatar é de quem não sou eu. */
  meuId: string;
  onAbrir: (id: string) => void;
  onFixar?: (id: string, fixar: boolean) => void;
  podeFixarMais: boolean;
  estado: EstadoDasExternas;
  podeAdministrar: boolean;
  onTentarDeNovo: () => void;
  temMais: boolean;
  carregandoMais: boolean;
  onCarregarMais: () => void;
  onNovaConversa?: () => void;
  onLimpar: () => void;
  /** As fixadas que não dá para mostrar agora (I1): "Conversa indisponível". */
  indisponiveis: readonly string[];
  /**
   * TUT-590 — a lista no chat rápido: o título "Conversas" com o id dela (a
   * caixa pode estar na tela ao mesmo tempo) e as ações do chat rápido no
   * lugar de "Nova conversa".
   */
  rapida?: { idDoTitulo: string; acoes: ReactNode };
}) {
  const { fixadas, demais } = separarFixadas(itens);
  const filtrando = recorte !== "todas" || canaisEscolhidos.length > 0 || busca.trim() !== "";
  // Re-revisão (M-C) — a indisponível não tem canal, dono nem texto para os
  // filtros: ela só aparece na lista inteira, como as demais que o filtro tira.
  const indisponiveisVisiveis = filtrando || !onFixar ? [] : indisponiveis;
  const idDasFixadas = rapida ? `${rapida.idDoTitulo}-fixadas` : "caixa-fixadas";
  const limpar = filtrando ? (
    <Button type="button" variant="link" size="sm" className="justify-self-start" onClick={onLimpar}>
      Limpar filtros
    </Button>
  ) : null;
  const item = (conversa: ConversaUnificada) => (
    <ItemDaCaixa
      key={conversa.id}
      conversa={conversa}
      meuId={meuId}
      aberta={conversa.id === aberta}
      podeFixar={podeFixarMais}
      onAbrir={onAbrir}
      onFixar={onFixar}
    />
  );
  return (
    <>
      <PanelHeader
        acoes={
          rapida ? (
            rapida.acoes
          ) : onNovaConversa ? (
            <IconButton label="Nova conversa" icone={<Plus aria-hidden="true" />} onClick={onNovaConversa} />
          ) : null
        }
      >
        <PanelTitle id={rapida ? rapida.idDoTitulo : "caixa-de-entrada"} icon={Inbox}>
          {rapida ? "Conversas" : "Caixa de entrada"}
        </PanelTitle>
      </PanelHeader>
      <div className="grid gap-2 px-3 pb-3">
        <ToolbarSearch>
          <Search aria-hidden="true" />
          <span className="sr-only">Buscar conversas</span>
          <input
            name="conversas-busca"
            type="search"
            value={busca}
            placeholder="Buscar nome ou mensagem"
            autoComplete="off"
            onChange={(evento) => onBusca(evento.target.value)}
          />
        </ToolbarSearch>
        <GrupoDeEscolha<RecorteDaCaixa>
          variante="pilula"
          rotulo="Recorte"
          value={recorte}
          onChange={onRecorte}
          className="w-full"
          opcoes={[
            { valor: "todas", rotulo: `Todas ${contagens.todas}`, nomeAcessivel: `Todas, ${contagens.todas}` },
            { valor: "minhas", rotulo: `Minhas ${contagens.minhas}`, nomeAcessivel: `Minhas, ${contagens.minhas}` },
            { valor: "naoLidas", rotulo: `Não lidas ${contagens.naoLidas}`, nomeAcessivel: `Não lidas, ${contagens.naoLidas}` },
          ]}
        />
        {/* TUT-590 — no chat rápido não há filtro de canal: fica o "Limpar". */}
        {rapida ? (
          limpar
        ) : (
        <fieldset className="m-0 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
          <legend className="sr-only">Canal</legend>
          {CANAIS_DA_CAIXA.filter((c) => canais.includes(c.canal)).map(({ canal, rotulo }) => {
            const Icone = ICONE_DO_CANAL[canal];
            return (
              <Button
                key={canal}
                type="button"
                variant="filtro"
                size="sm"
                aria-pressed={canaisEscolhidos.includes(canal)}
                data-canal={canal}
                onClick={() => onCanal(canal)}
              >
                <Icone aria-hidden="true" />
                {rotulo}
              </Button>
            );
          })}
          {limpar}
        </fieldset>
        )}
      </div>
      <AvisoDoAtendimento estado={estado} podeAdministrar={podeAdministrar} onTentarDeNovo={onTentarDeNovo} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {itens.length === 0 && indisponiveisVisiveis.length === 0 ? (
          <EmptyState>
            <div>
              <Inbox className="mx-auto size-8" aria-hidden="true" />
              <h3>{filtrando ? "Nada por aqui com estes filtros" : "Nenhuma conversa"}</h3>
              <p>
                {filtrando
                  ? "Troque o recorte ou limpe os filtros."
                  : onNovaConversa
                    ? "Comece uma em “Nova conversa”."
                    : "As conversas da equipe, do portal e do atendimento aparecem aqui."}
              </p>
            </div>
          </EmptyState>
        ) : (
          <>
            {fixadas.length || indisponiveisVisiveis.length ? (
              <div data-caixa-fixadas>
                <h3 id={idDasFixadas} className="m-0 px-4 pb-1 pt-2 text-legenda font-semibold text-[var(--muted)]">
                  Fixadas
                </h3>
                <ul aria-labelledby={idDasFixadas} className="m-0 list-none divide-y divide-border p-0">
                  {fixadas.map(item)}
                  {indisponiveisVisiveis.map((id) => (
                    <FixadaIndisponivel key={id} id={id} onDesafixar={() => onFixar?.(id, false)} />
                  ))}
                </ul>
              </div>
            ) : null}
            {demais.length ? (
              <ul aria-label="Conversas" className="m-0 list-none divide-y divide-border p-0">
                {demais.map(item)}
              </ul>
            ) : null}
          </>
        )}
        {/* M10 — fora do "vazio": com o filtro esvaziando a página, a conversa
            da página seguinte continua alcançável. */}
        {temMais ? (
          <div className="p-3">
            <Button type="button" variant="vidro" className="w-full" disabled={carregandoMais} onClick={onCarregarMais}>
              {carregandoMais ? "Carregando…" : "Carregar mais conversas do atendimento"}
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}
